import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  CircularProgress,
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { ExpandMore } from "@material-ui/icons";
import CompanyHeading from "../../../../theming/CompanyHeading";
import Loader from "../../../../theming/loader/Loader";
import MembershipsList from "./MembershipsList";
import { useAccordionState } from "../../../../../hooks/useAccordionState";
import { MembershipState } from "../../../../../redux/organizationMembership/types";
import { useState } from "react";
import { organizationService } from "../../../../../api";
import { fetchMembershipsByOrganizationId } from "../../../../../redux/organizationMembership/action";
import { useDispatch } from "react-redux";
import { enqueueSnackbar } from "notistack";
import { formatDateDDMMYYYY } from "../../../../../utils/formatDate";
import { showDialog } from "../../../../../redux/dialogs/actions";

/**
 * @interface MembershipsManagementProps
 */
interface MembershipsManagementProps {
  organizationId: string;
  memberships: MembershipState;
}

/**
 * Delays execution for a given number of milliseconds.
 * @param {number} ms Duration to sleep in milliseconds.
 * @returns {Promise<void>} A promise that resolves after the delay.
 */
function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}

/**
 * Polls a status function until the task is ready or a timeout occurs.
 * @param {() => Promise<{ ready: boolean; state: string; failed: boolean }>} getStatus
 *   Function that returns the current task state.
 * @param {number} timeoutMs Maximum time to wait before failing.
 * @param {number} intervalMs Delay between polls in milliseconds.
 * @returns {Promise<void>} A promise that resolves when the task succeeds.
 */
async function pollUntilReady(
  getStatus: () => Promise<{ ready: boolean; state: string; failed: boolean }>,
  timeoutMs = 15 * 60 * 1000,
  intervalMs = 5_000
) {
  const start = Date.now();

  while (Date.now() - start < timeoutMs) {
    const s = await getStatus();

    if (s.ready) {
      if (!s.failed && s.state === "SUCCESS") return;
      throw new Error(`Task failed: ${s.state}`);
    }

    await sleep(intervalMs);
  }

  throw new Error("Timed out waiting for start instructions generation");
}

/**
 * Triggers a browser download for a blob with a provided filename.
 * @param {Blob} blob The file content to download.
 * @param {string} filename The name to use for the downloaded file.
 * @returns {Promise<void>} A promise that resolves after the download is initiated.
 */
async function downloadBlobAsFile(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

/**
 * Delays execution for a given number of milliseconds.
 * @param {number} ms Duration to sleep in milliseconds.
 * @returns {Promise<void>} A promise that resolves after the delay.
 */
/**
 * Component for managing memberships within an organization.
 * @param {string} organizationId The ID of the organization.
 * @param {MembershipState} memberships The memberships of the organization.
 * @returns {JSX.Element} Rendered memberships management UI.
 */
export const MembershipsManagement = ({
  organizationId,
  memberships,
}: MembershipsManagementProps) => {
  const { t } = useTranslation(["common", "organization", "snackbars"]);
  const dispatch = useDispatch();

  const [expanded, handleChange] = useAccordionState("expandedPanel");
  const [downloadingStartInstructions, setDownloadingStartInstructions] =
    useState(false);

  const membershipsNeedStartInstructions = memberships.memberships.filter(
    (m) => m.user_needs_start_instruction
  );

  const handleDownloadStartInstructionsConfirm = async (membershipIds: string[]) => {
    try {
      setDownloadingStartInstructions(true);

      // 1) fire (POST /organizations/:orgId/generate-start-instructions)
      const { task_id } = await organizationService.startStartInstructions(
        organizationId,
        membershipIds
      );

      // 2) poll (GET /organizations/:orgId/start-instructions/:taskId/status)
      await pollUntilReady(() =>
        organizationService.getStartInstructionsStatus(organizationId, task_id)
      );

      // 3) download (GET /organizations/:orgId/download-start-instructions/:taskId/download)
      const { blob, filename } =
        await organizationService.downloadStartInstructionsByTaskId(
          organizationId,
          task_id
        );

      await downloadBlobAsFile(
        blob,
        filename || `Startanleitungen_${formatDateDDMMYYYY()}.pdf`
      );

      dispatch(fetchMembershipsByOrganizationId(organizationId));
    } catch (err) {
      enqueueSnackbar(
        t("organization.downloadStartInstructionsFail", { ns: "snackbars" }),
        { variant: "error" }
      );
    } finally {
      setDownloadingStartInstructions(false);
    }
  };

  const handleDownloadStartInstructions = () => {
    const membershipIds = membershipsNeedStartInstructions.map((m) => m.id);

    dispatch(
      showDialog(
        {
          title: t("membership.startInstructionsDialog.title", {
            ns: "organization",
          }),
          message: (
            <div>
              <p>
                {t("membership.startInstructionsDialog.body", {
                  ns: "organization",
                })}
              </p>
            </div>
          ),
          confirmButtonText: t("forms.buttons.confirm", { ns: "common" }),
          action: () => handleDownloadStartInstructionsConfirm(membershipIds),
        },
        true
      )
    );
  };

  return (
    <Box>
      <CompanyHeading>
        {t("membership.membersOverview", { ns: "organization" })}
      </CompanyHeading>

      {memberships?.isLoading ? (
        <Loader />
      ) : (
        <>
          <Box
            my={2}
            display="flex"
            justifyContent={{ xs: "flex-start", sm: "flex-end" }}
          >
            <Button
              onClick={handleDownloadStartInstructions}
              variant="contained"
              color="primary"
              disabled={
                downloadingStartInstructions || !membershipsNeedStartInstructions.length
              }
              style={{ whiteSpace: "normal", wordBreak: "break-word" }}
            >
              {downloadingStartInstructions ? (
                <Box display="flex" alignItems="center">
                  <CircularProgress size={20} style={{ marginRight: ".625rem" }} />
                  {t("membership.downloadingStartInstructions", {
                    ns: "organization",
                  })}
                </Box>
              ) : (
                t("membership.downloadStartInstructions", {
                  count: membershipsNeedStartInstructions.length || 0,
                  ns: "organization",
                })
              )}
            </Button>
          </Box>

          <Accordion
            expanded={expanded === "memberships"}
            onChange={handleChange("memberships")}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              aria-controls="panel1-content"
              id="panel1-header"
            >
              {t("membership.memberships", {
                ns: "organization",
                number: memberships?.memberships?.length || 0,
              })}
            </AccordionSummary>

            <AccordionDetails>
              {memberships?.memberships?.length > 0 ? (
                <MembershipsList
                  memberships={memberships.memberships}
                  organizationId={organizationId}
                />
              ) : (
                <Typography>
                  {t("membership.noMemberships", { ns: "organization" })}
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
        </>
      )}
    </Box>
  );
};
